/**
 * Logger Service
 * 
 * Centralized logging service that respects environment configuration.
 * In production, console logs are disabled and errors are sent to tracking service.
 * 
 * @remarks
 * - Use this instead of console.log throughout the application
 * - Automatically disabled in production
 * - Integrates with error tracking services
 */

import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export enum LogLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly minLevel: LogLevel = environment.production ? LogLevel.Warn : LogLevel.Debug;
  private readonly enableConsole: boolean = environment.logging.enableConsoleLogging;

  /**
   * Logs debug information (only in development)
   */
  debug(message: string, ...args: any[]): void {
    this.log(LogLevel.Debug, message, args);
  }

  /**
   * Logs informational messages
   */
  info(message: string, ...args: any[]): void {
    this.log(LogLevel.Info, message, args);
  }

  /**
   * Logs warnings
   */
  warn(message: string, ...args: any[]): void {
    this.log(LogLevel.Warn, message, args);
  }

  /**
   * Logs errors
   */
  error(message: string, error?: any, ...args: any[]): void {
    this.log(LogLevel.Error, message, [error, ...args]);
    
    // Send to error tracking service in production
    if (environment.logging.enableErrorTracking) {
      this.sendToErrorTracking(message, error);
    }
  }

  /**
   * Core logging method
   * @private
   */
  private log(level: LogLevel, message: string, args: any[]): void {
    if (level < this.minLevel || !this.enableConsole) {
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${LogLevel[level]}]`;

    switch (level) {
      case LogLevel.Debug:
        console.debug(prefix, message, ...args);
        break;
      case LogLevel.Info:
        console.info(prefix, message, ...args);
        break;
      case LogLevel.Warn:
        console.warn(prefix, message, ...args);
        break;
      case LogLevel.Error:
        console.error(prefix, message, ...args);
        break;
    }
  }

  /**
   * Sends errors to tracking service (Sentry, etc.)
   * @private
   */
  private sendToErrorTracking(message: string, error?: any): void {
    // TODO: Integrate with Sentry or similar service
    // if (environment.logging.sentryDsn) {
    //   Sentry.captureException(error, {
    //     extra: { message }
    //   });
    // }
  }
}






